import React, { useEffect, useMemo } from 'react';
import CompaniesCore from '../components/CompaniesCore';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { useIntl } from 'umi';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { GAME_TYPES } from '@/pages/intelligence/components/TeaSelector/GameTypeSelector';
import { handleCompiniesFilterParamsToApi } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import useFilterOptions from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/hooks/useFilterOptions';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { COMPANIES_DEFAULT_FILTER_VALUE } from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/constants';
import moment from 'moment';
import {
  getTopCompaniesCustomValidUrlParams,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import { useShortLink } from '@intelligence/hooks/useShortLink';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import { getRevenuePageValidCfg } from '../utils';

export default function Revenue() {
  const { formatMessage } = useIntl();
  // 打点信息
  const pageLogInfo = dbiPageLogMes?.topchartsCompanyRevenue;
  // 获取国家筛选器接口返回的列表
  const { loading, regionOptions, headQuatersOptionsData } = useFilterOptions();
  // 获取接口返回的默认有数据的时间
  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.companiesRevenue?.type,
  });
  // 是否有短链接更新功能
  const hasUrlShortLink = hasShortLinkRatio.topCompaniesRevenue;

  const {
    initShortLinkCfg,
    shortLinkLoading,
    urlCustomParams,
    shortLinkFilterValue,
    shortLinkValidQuery,
  } = useShortLink({
    hasUrlShortLink,
    getPageValidCfg: getRevenuePageValidCfg,
    getCustomValidUrlParams: getTopCompaniesCustomValidUrlParams,
  });
  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initShortLinkCfg();
  }, []);

  // 筛选器默认值
  const defaultFilterValue = useMemo(
    () => ({
      ...COMPANIES_DEFAULT_FILTER_VALUE,
      [filterKeyMap.GAME_TYPE]: {
        value: [...GAME_TYPES],
      },
    }),
    [],
  );

  const filterCfg: TopchartFilterCfgType = useMemo(
    () => ({
      // 筛选器列表条目按顺序配置
      filterKeyList: [
        filterKeyMap.MY_FILTER,
        filterKeyMap.GAME_TYPE,
        filterKeyMap.REVENUE_RANGE_UNIT,
        filterKeyMap.RANGE_TIME,
        filterKeyMap.DATASOURCESIM,
        filterKeyMap.REGION,
        filterKeyMap.IPO_STATUS,
        filterKeyMap.COUNTRY,
      ],
      // 筛选器默认值
      defaultValue: defaultFilterValue,
      // reset筛选器展示的默认值
      resetFilterValue: {
        ...defaultFilterValue,
        [filterKeyMap.RANGE_TIME]: {
          type: 'month',
          value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
        },
      },
      // 筛选器的差异化从这里传参去改变
      specialParams: {
        // 国家筛选器options,
        regionOptions,
        // 没取到交集用的默认值
        defalutMarket: ['global'],
        disableNoChina: true,
        isTopCompanies: true,
        showSelectType: true,
        startOfDate: '2013-01-01',
        rangeTimeType: FilterTimeType.dayWeekMonthYearRange,
        typeList: ['month', 'year'], // 'date', 'week',
        disableIegg: true,
        disableAnnie: false,
        [filterKeyMap.COUNTRY]: {
          value: headQuatersOptionsData?.map((item) => item?.value) || [],
          options: headQuatersOptionsData,
        },
      },
      iconText: (
        <div>
          <div>{formatMessage({ id: 'dbi_company_filter_tips_1_row' })}</div>
          <div>{formatMessage({ id: 'dbi_company_filter_tips_2_row' })}</div>
          <div>{formatMessage({ id: 'dbi_company_filter_tips_3_row' })}</div>
          <div>{formatMessage({ id: 'dbi_company_filter_tips_4_row' })}</div>
        </div>
      ),
      showConfirmButton: true,
      haveFilterSaveBtn: true,
      handleFilterToApiParams: handleCompiniesFilterParamsToApi,
      hasUrlShortLink,
    }),
    [hasUrlShortLink, defaultFilterValue, formatMessage, headQuatersOptionsData, regionOptions],
  );
  return (
    <>
      <MarqueeBanner />
      <CompaniesCore
        topChartsType="top_companies_revenue"
      pageLogInfo={pageLogInfo}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      headQuatersOptionsData={headQuatersOptionsData}
      filterLoading={loading || shortLinkLoading}
      filterCfg={filterCfg}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
